<template>
  <main class="imprint-page">
    <header class="site-header">
      <img
        class="site-logo"
        :src="logo"
        alt="BOTTIMMO Turkoglu"
      />
    </header>

    <section class="hero">
      <h1>{{ $t('impressum-page.title') }}</h1>
    </section>

    <section class="content">
      <div class="content-container">
        <section class="legal-section">
          <h2>{{ $t('impressum-page.imprint.title') }}</h2>

          <p>{{ $t('impressum-page.imprint.intro') }}</p>

          <div class="imprint-table">
            <div class="imprint-table__header">
              <strong>{{ $t('impressum-page.imprint.table.headers.label') }}</strong>
              <strong>{{ $t('impressum-page.imprint.table.headers.value') }}</strong>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.trade-name.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.trade-name.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.business-name.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.business-name.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.address.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.address.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.representative.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.representative.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.mersis.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.mersis.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.trade-register.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.trade-register.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.tax.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.tax.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>
                {{ $t('impressum-page.imprint.table.professional-organization.label') }}
              </strong>
              <span>
                {{ $t('impressum-page.imprint.table.professional-organization.value') }}
              </span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.email.label') }}</strong>
              <a href="mailto:turkiye@immoviewer.tech">
                {{ $t('impressum-page.imprint.table.email.value') }}
              </a>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.phone.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.phone.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.kep.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.kep.value') }}</span>
            </div>

            <div class="imprint-table__row">
              <strong>{{ $t('impressum-page.imprint.table.website.label') }}</strong>
              <span>{{ $t('impressum-page.imprint.table.website.value') }}</span>
            </div>
          </div>

          <p>
            <strong>{{ $t('impressum-page.imprint.license.label') }}</strong>
            {{ $t('impressum-page.imprint.license.content') }}
          </p>

          <p>
            <strong>{{ $t('impressum-page.imprint.commercial-communication.label') }}</strong>
            {{ $t('impressum-page.imprint.commercial-communication.content') }}
          </p>
        </section>

        <section class="legal-section">
          <h2>{{ $t('impressum-page.liability.title') }}</h2>

          <h3>{{ $t('impressum-page.liability.content-liability.title') }}</h3>

          <p>{{ $t('impressum-page.liability.content-liability.content.0') }}</p>
          <p>{{ $t('impressum-page.liability.content-liability.content.1') }}</p>
          <p>{{ $t('impressum-page.liability.content-liability.content.2') }}</p>

          <h3>{{ $t('impressum-page.liability.links.title') }}</h3>

          <p>{{ $t('impressum-page.liability.links.content.0') }}</p>
          <p>{{ $t('impressum-page.liability.links.content.1') }}</p>

          <h3>{{ $t('impressum-page.liability.copyright.title') }}</h3>

          <p>{{ $t('impressum-page.liability.copyright.content.0') }}</p>
          <p>{{ $t('impressum-page.liability.copyright.content.1') }}</p>
          <p>{{ $t('impressum-page.liability.copyright.content.2') }}</p>
        </section>
      </div>
    </section>

    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-links">
          <strong>{{ $t('impressum-page.section-footer.title') }}</strong>

          <a
            target="_blank"
            :href="imprintUrl"
            rel="noopener noreferrer"
          >
            {{ $t('impressum-page.section-footer.content.0') }}
          </a>

          <a
            target="_blank"
            :href="publicDataPrivacyUrl"
            rel="noopener noreferrer"
          >
            {{ $t('impressum-page.section-footer.content.1') }}
          </a>

          <a
            target="_blank"
            :href="publicDataPrivacyUrl"
            rel="noopener noreferrer"
          >
            {{ $t('impressum-page.section-footer.content.2') }}
          </a>
        </div>

        <div class="footer-divider" />

        <div class="footer-company">
          © {{ currentYear }} {{ $t('impressum-page.section-footer.content.3') }}
          <span class="separator">·</span>
          {{ $t('impressum-page.section-footer.content.4') }}
          <br />
          {{ $t('impressum-page.section-footer.content.5') }}
        </div>
      </div>
    </footer>
  </main>
</template>

<script setup>
import { computed } from 'vue'

import logo from '@/assets/img/logo.png'
import { useBranding } from '@/composables/useBranding'

const currentYear = computed(() => new Date().getFullYear())
const { publicDataPrivacyUrl, imprintUrl } = useBranding()
</script>

<style scoped>
.imprint-page {
  min-height: 100vh;
  background: #fff;
  color: #303030;
  font-family: Inter, Arial, Helvetica, sans-serif;
}

.site-header {
  height: 82px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-top: 1px solid #e5e5e5;
}

.site-logo {
  display: block;
  width: 300px;
  max-width: 70%;
  max-height: 58px;
  object-fit: contain;
}

.hero {
  min-height: 400px;
  background: #00150d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
}

.hero h1 {
  margin: 0;
  color: #fff;
  font-size: clamp(40px, 4vw, 64px);
  font-weight: 700;
  line-height: 1.1;
}

.content {
  width: 100%;
}

.content-container {
  width: min(100% - 80px, 1240px);
  margin: 0 auto;
  padding: 38px 0 42px;
}

.legal-section {
  margin-bottom: 38px;
}

.legal-section:last-child {
  margin-bottom: 0;
}

.legal-section h2 {
  margin: 0 0 20px;
  text-align: center;
  color: #303030;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.25;
}

.legal-section h3 {
  margin: 32px 0 14px;
  color: #303030;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
}

.legal-section p {
  margin: 0 0 16px;
  color: #8d8d8d;
  font-size: 24px;
  line-height: 1.48;
}

.legal-section p strong {
  color: #303030;
  font-weight: 700;
}

.content a {
  color: inherit;
  text-decoration: none;
}

.content a:hover {
  text-decoration: underline;
}

.imprint-table {
  width: 100%;
  margin: 24px 0 32px;
  border-top: 1px solid #e5e5e5;
}

.imprint-table__header,
.imprint-table__row {
  display: grid;
  grid-template-columns: minmax(220px, 32%) 1fr;
}

.imprint-table__header {
  background: #00150d;
  color: #fff;
  font-size: 20px;
  line-height: 1.4;
}

.imprint-table__header > *,
.imprint-table__row > * {
  padding: 12px 16px;
}

.imprint-table__row {
  border-bottom: 1px solid #e5e5e5;
  color: #8d8d8d;
  font-size: 20px;
  line-height: 1.45;
}

.imprint-table__row strong {
  color: #303030;
  font-weight: 700;
}

.imprint-table__row a {
  color: inherit;
  text-decoration: none;
}

.imprint-table__row a:hover {
  text-decoration: underline;
}

.site-footer {
  width: 100%;
  border-top: 1px solid #ededed;
  background: #fff;
}

.footer-container {
  width: min(100% - 80px, 1120px);
  margin: 0 auto;
  padding: 56px 0 32px;
}

.footer-links {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  font-size: 20px;
  line-height: 1.35;
}

.footer-links strong {
  font-weight: 700;
}

.footer-links a {
  color: inherit;
  text-decoration: none;
}

.footer-links a:hover {
  text-decoration: underline;
}

.footer-divider {
  height: 1px;
  width: 100%;
  background: #e5e5e5;
  margin: 42px 0 28px;
}

.footer-company {
  color: #696969;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
}

.separator {
  display: inline-block;
  padding: 0 8px;
}

@media (max-width: 900px) {
  .site-header {
    height: 72px;
  }

  .site-logo {
    width: 240px;
  }

  .hero {
    min-height: 300px;
  }

  .content-container {
    width: min(100% - 48px, 1240px);
    padding-top: 34px;
  }

  .legal-section h2 {
    font-size: 24px;
  }

  .legal-section h3 {
    font-size: 21px;
  }

  .legal-section p {
    font-size: 19px;
  }

  .imprint-table__header,
  .imprint-table__row {
    font-size: 18px;
  }

  .footer-container {
    width: min(100% - 48px, 1120px);
  }
}

@media (max-width: 600px) {
  .site-header {
    height: 64px;
    padding: 10px 16px;
  }

  .site-logo {
    width: 200px;
  }

  .hero {
    min-height: 220px;
    padding: 40px 20px;
  }

  .hero h1 {
    font-size: 36px;
  }

  .content-container {
    width: calc(100% - 32px);
    padding: 28px 0 32px;
  }

  .legal-section {
    margin-bottom: 32px;
  }

  .legal-section h2 {
    margin-bottom: 16px;
    font-size: 21px;
  }

  .legal-section h3 {
    margin-top: 26px;
    margin-bottom: 10px;
    font-size: 19px;
  }

  .legal-section p {
    font-size: 17px;
    line-height: 1.5;
  }

  .imprint-table {
    margin: 20px 0 28px;
  }

  .imprint-table__header,
  .imprint-table__row {
    grid-template-columns: 1fr;
  }

  .imprint-table__header {
    font-size: 17px;
  }

  .imprint-table__header strong:last-child {
    display: none;
  }

  .imprint-table__row {
    font-size: 16px;
  }

  .imprint-table__header > *,
  .imprint-table__row > * {
    padding: 10px 12px;
  }

  .imprint-table__row strong {
    padding-bottom: 2px;
  }

  .imprint-table__row strong + * {
    padding-top: 2px;
  }

  .footer-container {
    width: calc(100% - 32px);
    padding: 40px 0 24px;
  }

  .footer-links {
    font-size: 17px;
  }

  .footer-divider {
    margin: 32px 0 22px;
  }

  .footer-company {
    font-size: 14px;
  }

  .separator {
    padding: 0 4px;
  }
}
</style>
